import { debounce } from 'lodash-es';
import { GlButton, GlTooltipDirective as GlTooltip, GlSprintf } from '../../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import { NodeViewWrapper, NodeViewContent } from '../../../../../../node_modules/@tiptap/vue-2/dist/index.js?vue3';
import { DEFAULT_DEBOUNCE_AND_THROTTLE_MS } from '~/lib/utils/constants';
import SandboxedMermaid from '../../../behaviors/components/sandboxed_mermaid.vue?vue3';
import codeBlockLanguageLoader from '../../services/code_block_language_loader';
import EditorStateObserver from '../editor_state_observer.vue?vue3';
import { memoizedGet } from '../../services/utils.js?vue3';
import { lineOffsetToLangParams, langParamsToLineOffset, toAbsoluteLineOffset, getLines, appendNewlines } from '../../services/code_suggestion_utils';
export default {
    name: 'CodeBlock',
    components: {
        GlButton,
        GlSprintf,
        NodeViewWrapper,
        NodeViewContent,
        EditorStateObserver,
        SandboxedMermaid
    },
    directives: {
        GlTooltip
    },
    inject: [
        'contentEditor'
    ],
    props: {
        editor: {
            type: Object,
            required: true
        },
        node: {
            type: Object,
            required: true
        },
        updateAttributes: {
            type: Function,
            required: true
        }
    },
    data () {
        return {
            diagramUrl: '',
            diagramSource: '',
            allLines: [],
            deletedLines: [],
            addedLines: []
        };
    },
    computed: {
        isCodeSuggestion () {
            return this.node.attrs.isCodeSuggestion && this.contentEditor.codeSuggestionsConfig?.canSuggest;
        },
        canExpandSuggestion () {
            return Boolean(this.contentEditor.codeSuggestionsConfig.blobRawPath);
        },
        classList () {
            return this.isCodeSuggestion ? '!gl-p-0 suggestion-added-input' : `gl-p-3 code highlight code-syntax-highlight-theme`;
        },
        lineOffset () {
            return langParamsToLineOffset(this.node.attrs.langParams);
        },
        absoluteLineOffset () {
            const { lineRange } = this.contentEditor.codeSuggestionsConfig;
            if (!lineRange) return [
                0,
                0
            ];
            return toAbsoluteLineOffset(this.lineOffset, lineRange.end);
        },
        disableDecrementLineStart () {
            return this.absoluteLineOffset[0] <= 1;
        },
        disableIncrementLineStart () {
            return this.lineOffset[0] >= 0;
        },
        disableDecrementLineEnd () {
            return this.lineOffset[1] <= 0;
        },
        disableIncrementLineEnd () {
            return this.absoluteLineOffset[1] >= this.allLines.length - 1;
        }
    },
    async mounted () {
        if (this.isCodeSuggestion) {
            if (this.contentEditor.codeSuggestionsConfig.blobRawPath) await this.updateAllLines();
            this.updateCodeSuggestion();
        }
        this.updateCodeBlock = debounce(this.updateCodeBlock, DEFAULT_DEBOUNCE_AND_THROTTLE_MS);
        const lang = codeBlockLanguageLoader.findOrCreateLanguageBySyntax(this.node.attrs.language);
        await codeBlockLanguageLoader.loadLanguage(lang.syntax);
        this.updateAttributes({
            language: this.node.attrs.language
        });
    },
    methods: {
        async updateAllLines () {
            const rawPath = this.contentEditor.codeSuggestionsConfig.blobRawPath;
            const content = await memoizedGet(rawPath).catch(()=>null);
            if (content !== null) this.allLines = content.split('\n');
        },
        updateCodeSuggestion () {
            this.deletedLines = appendNewlines(getLines(this.absoluteLineOffset, this.allLines));
            this.addedLines = appendNewlines(this.$refs.nodeViewContent?.$el.textContent.split('\n') || []);
        },
        updateNodeView () {
            if (this.isCodeSuggestion) {
                this.updateCodeSuggestion();
            } else {
                this.updateCodeBlock();
            }
        },
        async updateCodeBlock () {
            if (!this.node.attrs.showPreview) {
                this.diagramSource = '';
                return;
            }
            if (!this.editor.isActive('diagram')) return;
            this.diagramSource = this.$refs.nodeViewContent?.$el.textContent || '';
            if (this.node.attrs.language !== 'mermaid') {
                this.diagramUrl = await this.contentEditor.renderDiagram(this.diagramSource, this.node.attrs.language);
            }
        },
        updateLineOffset (deltaStart = 0, deltaEnd = 0) {
            const { lineOffset } = this;
            this.editor.chain().updateAttributes('codeSuggestion', {
                langParams: lineOffsetToLangParams([
                    lineOffset[0] + deltaStart,
                    lineOffset[1] + deltaEnd
                ])
            }).run();
        }
    }
};
